<script setup lang="ts">
/*
 * Galerie d'images secondaires d'un produit, ou d'une déclinaison quand
 * variantId est fourni — mêmes 4 opérations (liste/upload/réordonner/
 * supprimer), juste des endpoints différents (ProductImageService). Une
 * galerie de déclinaison vide signifie qu'elle hérite de celle du produit ;
 * ce panneau montre toujours ce qui existe réellement pour CE porteur, sans
 * appliquer lui-même l'héritage (l'API le fait à la lecture du produit).
 */
import { computed, ref } from 'vue';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import { token } from '@/refs/common.refs';
import { ProductImageService } from '@/services/product-image.services';
import ProductGalleryGrid from './ProductGalleryGrid.vue';
import type { IProductGalleryImage } from '@/types/product-image.types';

const props = defineProps<{
  productId: string;
  variantId?: string;
}>();

const MAX_IMAGES = 5;

const queryClient = useQueryClient();

const queryKey = computed(() => [
  'product-gallery',
  props.productId,
  props.variantId ?? null,
]);

const {
  data: images,
  isPending: isLoading,
  isError,
} = useQuery({
  queryKey,
  queryFn: () =>
    props.variantId
      ? ProductImageService.listVariantImages(props.productId, props.variantId)
      : ProductImageService.listImages(props.productId),
  enabled: computed(() => !!props.productId),
});

const gallery = computed(
  () =>
    images.value?.slice().sort((a, b) => a.displayOrder - b.displayOrder) ?? [],
);

function invalidateGallery() {
  return queryClient.invalidateQueries({ queryKey: queryKey.value });
}

/* ── Upload ─────────────────────────────────────────────────────────────── */
const uploading = ref(false);
const uploadError = ref('');

async function uploadOne(file: File): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  const url = props.variantId
    ? `${VITE_API_URL}products/${props.productId}/variants/${props.variantId}/images`
    : `${VITE_API_URL}products/${props.productId}/images`;
  await axios.post(url, fd, {
    headers: { Authorization: `Bearer ${token.value?.accessToken}` },
  });
}

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = '';
  if (!files.length) return;

  const room = MAX_IMAGES - gallery.value.length;
  if (room <= 0) {
    uploadError.value = `La galerie est déjà pleine (${MAX_IMAGES} images maximum).`;
    return;
  }
  const toUpload = files.slice(0, room);
  if (files.length > toUpload.length) {
    uploadError.value = `Seules ${toUpload.length} image(s) sur ${files.length} seront ajoutées (limite de ${MAX_IMAGES} images).`;
  } else {
    uploadError.value = '';
  }

  uploading.value = true;
  toUpload
    .reduce(
      (chain, file) => chain.then(() => uploadOne(file)),
      Promise.resolve(),
    )
    .then(() => invalidateGallery())
    .catch((err) => {
      const raw = err?.response?.data?.message;
      uploadError.value = Array.isArray(raw)
        ? raw.join(' · ')
        : (raw ?? err?.message ?? "Erreur lors de l'upload");
    })
    .finally(() => {
      uploading.value = false;
    });
}

/* ── Suppression ────────────────────────────────────────────────────────── */
const pendingDeleteId = ref<string | null>(null);
const deleteError = ref('');

async function removeImage(image: IProductGalleryImage) {
  pendingDeleteId.value = image.id;
  deleteError.value = '';
  try {
    if (props.variantId) {
      await ProductImageService.removeVariantImage(
        props.productId,
        props.variantId,
        image.id,
      );
    } else {
      await ProductImageService.removeImage(props.productId, image.id);
    }
    await invalidateGallery();
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    deleteError.value = Array.isArray(raw)
      ? raw.join(' · ')
      : (raw ?? err?.message ?? 'Impossible de supprimer cette image');
  } finally {
    pendingDeleteId.value = null;
  }
}

/* ── Réordonnancement ───────────────────────────────────────────────────── */
const reordering = ref(false);
const reorderError = ref('');

async function persistOrder(orderedIds: string[]) {
  reordering.value = true;
  reorderError.value = '';
  try {
    if (props.variantId) {
      await ProductImageService.reorderVariantImages(
        props.productId,
        props.variantId,
        orderedIds,
      );
    } else {
      await ProductImageService.reorderImages(props.productId, orderedIds);
    }
    await invalidateGallery();
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    reorderError.value = Array.isArray(raw)
      ? raw.join(' · ')
      : (raw ?? err?.message ?? 'Impossible de réordonner la galerie');
  } finally {
    reordering.value = false;
  }
}

function moveImage(id: string, direction: -1 | 1) {
  const items = gallery.value.slice();
  const idx = items.findIndex((m) => m.id === id);
  const targetIdx = idx + direction;
  if (idx < 0 || targetIdx < 0 || targetIdx >= items.length) return;
  [items[idx], items[targetIdx]] = [items[targetIdx], items[idx]];
  void persistOrder(items.map((m) => m.id));
}
</script>

<template>
  <div class="pgp">
    <div v-if="isLoading" class="pgp__loading">Chargement de la galerie…</div>
    <div v-else-if="isError" class="pgp__empty">
      <i class="pi pi-exclamation-circle" />
      <p>Impossible de charger la galerie.</p>
    </div>

    <template v-else>
      <p v-if="uploadError" class="pgp__error">{{ uploadError }}</p>
      <p v-if="deleteError" class="pgp__error">{{ deleteError }}</p>
      <p v-if="reorderError" class="pgp__error">{{ reorderError }}</p>

      <div class="pgp__hd">
        <p class="pgp__hint">
          {{ gallery.length }}/{{ MAX_IMAGES }} images — la première devient
          l'image principale{{ variantId ? ' de cette déclinaison' : '' }}.
        </p>
        <label
          class="pgp__btn"
          :class="{ 'pgp__btn--disabled': gallery.length >= MAX_IMAGES }"
        >
          <i v-if="uploading" class="pi pi-spin pi-spinner" />
          <i v-else class="pi pi-plus" />
          Ajouter des images
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            :disabled="uploading || gallery.length >= MAX_IMAGES"
            @change="onFileChange"
          />
        </label>
      </div>

      <p v-if="!gallery.length" class="pgp__gallery-empty">
        Aucune image secondaire{{ variantId ? ' pour cette déclinaison' : '' }}.
      </p>
      <ProductGalleryGrid
        v-else
        :items="gallery"
        :pending-delete-id="pendingDeleteId"
        :reordering="reordering"
        @move-up="(id) => moveImage(id, -1)"
        @move-down="(id) => moveImage(id, 1)"
        @remove="removeImage"
      />
    </template>
  </div>
</template>

<style scoped>
.pgp {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.pgp__loading,
.pgp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.pgp__empty i {
  font-size: 1.75rem;
  opacity: 0.4;
}
.pgp__error {
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.1);
  color: var(--nxt-error);
  font-size: 0.8125rem;
}
.pgp__hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.pgp__hint {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.pgp__gallery-empty {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.pgp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.125rem;
  padding: 0 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: #ffd60a;
  color: #0b1320;
  flex-shrink: 0;
}
.pgp__btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
